import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import { STAFF_QUOTE_CATALOG } from '@staff-app/graphql-client';
import { ENTERPRISE_PLAN } from '../pricing/plans';
import type {
  CatalogAddOn,
  CatalogPlan,
  CatalogProduct,
  CatalogShipping,
  QuoteCatalog,
} from '../pricing/types';

/** Wire shape: nested price maps arrive stringified. See quote.response.ts. */
interface RawCatalog {
  products: (Omit<CatalogProduct, 'prices'> & { pricesJson?: string | null })[];
  shippings: (Omit<CatalogShipping, 'prices'> & { pricesJson?: string | null })[];
  plans: (Omit<CatalogPlan, 'plans'> & { plansJson?: string | null })[];
  addOns: (Omit<CatalogAddOn, 'prices' | 'stripeRates'> & {
    pricesJson?: string | null;
    stripeRatesJson?: string | null;
  })[];
  plansCollection: string;
}

/**
 * Parse a price map defensively. A malformed map degrades that ONE product to unpriced
 * (the engine then flags it) rather than throwing and taking down the whole page — the
 * operator can still quote everything else while we look at the bad document.
 */
function parseMap<T>(json: string | null | undefined): T | undefined {
  if (!json) return undefined;
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as T) : undefined;
  } catch {
    return undefined;
  }
}

const EMPTY: QuoteCatalog = {
  products: [],
  shippings: [],
  plans: [],
  addOns: [],
  plansCollection: '',
};

/**
 * Loads the live pricing catalog once per page visit.
 *
 * `cache-and-network` on purpose: a price change in `deviceProducts` must reach the next
 * quote, but the operator shouldn't stare at a spinner when they reopen the tool. Prices
 * are never cached across a reload.
 */
export function useQuoteCatalog() {
  const { data, loading, error, refetch } = useQuery<{ staffQuoteCatalog: RawCatalog }>(
    STAFF_QUOTE_CATALOG,
    { fetchPolicy: 'cache-and-network' },
  );

  const catalog = useMemo<QuoteCatalog>(() => {
    const raw = data?.staffQuoteCatalog;
    if (!raw) return EMPTY;
    return {
      products: (raw.products ?? []).map(({ pricesJson, ...p }) => ({
        ...p,
        prices: parseMap<Record<string, number | null>>(pricesJson),
      })),
      shippings: (raw.shippings ?? []).map(({ pricesJson, ...s }) => ({
        ...s,
        prices: parseMap<CatalogShipping['prices']>(pricesJson),
      })),
      plans: [
        ...(raw.plans ?? []).map(({ plansJson, ...p }) => ({
          ...p,
          plans: parseMap<CatalogPlan['plans']>(plansJson),
        })),
        ENTERPRISE_PLAN,
      ],
      addOns: (raw.addOns ?? []).map(({ pricesJson, stripeRatesJson, ...a }) => ({
        ...a,
        prices: parseMap<CatalogAddOn['prices']>(pricesJson),
        stripeRates: parseMap<CatalogAddOn['stripeRates']>(stripeRatesJson),
      })),
      plansCollection: raw.plansCollection ?? '',
    };
  }, [data]);

  return { catalog, loading, error, refetch };
}
